import {createContext, lazy, Suspense, useContext, useEffect, useMemo, useRef} from 'react';
import {Route, Switch, useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import RouteKey from './key';
import {useAuth} from '../modules/profile/logic/auth';
import {LocationState} from '../models/Location';

const UnlockAccount = lazy(() => import('../pages/auth/UnlockAccount'));
const Login = lazy(() => import('../pages/auth/Login'));
const OTP = lazy(() => import('../pages/auth/OTP'));
const CreateAccountManagement = lazy(
    () => import('../modules/admin/components/CreateAccountManagement')
);
const ActivateAccount = lazy(() => import('../pages/auth/ActivateAccount'));
const SuccessfulAccountCreationPage = lazy(
    () => import('../pages/auth/SuccessfulAccountCreationPage')
);
const UpdatePassword = lazy(() => import('../pages/auth/UpdatePassword'));
const ForgotUsername = lazy(() => import('../pages/auth/ForgotUsername'));
const ForgotPassword = lazy(() => import('../pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/auth/ResetPassword'));
const DefaultHeaderEmpty = lazy(() => import('../components/DefaultHeaderEmpty'));
const SetPassword = lazy(() => import('../pages/auth/SetPassword'));
const Activated = lazy(() => import('../pages/auth/Activated'));

const {error: errorRoute} = RouteKey.child;
const {
  createAccount,
  successfulAccountCreation,
  login,
  forgotUsername,
  forgotPassword,
  otp,
  resetPassword,
  setPassword,
  activateAccount,
  activated,
  updatePassword,
  unlockAccount,
} = RouteKey.auth.child;

const AuthNavigationContext = createContext<{
  toForgetPassword: string;
  toActivateAccount: string;
}>({
  toForgetPassword: '',
  toActivateAccount: '',
});

export const useAuthNavigation = () => useContext(AuthNavigationContext);

const NavigateOnce = ({to}: {to: string}) => {
  const history = useHistory();
  const location = useLocation();
  const hasNavigated = useRef(false);

  useEffect(() => {
    const current = `${location.pathname}${location.search}`;
    if (hasNavigated.current || to === current) {
      return;
    }
    hasNavigated.current = true;
    history.replace(to);
  }, [history, location.pathname, location.search, to]);

  return null;
};

const AuthRoute = () => {
  const {path, url} = useRouteMatch();
  const auth = useAuth();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const hasBypassRedirected = useRef(false);
  const {from, isUserPasswordExpired} = location?.state || {from: {pathname: '/', search: '?', message: null}};

  const resolveSafePostLoginRedirect = () => {
    const pathname = from?.pathname || '/';
    const search = from?.search && from.search !== '?' ? from.search : '';
    const target = `${pathname}${search}`;

    // Avoid auth-to-auth and self redirects that can trigger router update loops.
    if (pathname.startsWith('/auth') || target === `${location.pathname}${location.search}`) {
      return '/';
    }
    return target;
  };

  const shouldBypassAuthPages =
      auth.isUserLoggedIn &&
      (!location.pathname.startsWith(`${path}/${updatePassword.route}`) || !isUserPasswordExpired);

  const safePostLoginRedirect = resolveSafePostLoginRedirect();

  useEffect(() => {
    if (!shouldBypassAuthPages) {
      hasBypassRedirected.current = false;
      return;
    }

    if (hasBypassRedirected.current || safePostLoginRedirect === `${location.pathname}${location.search}`) {
      return;
    }

    hasBypassRedirected.current = true;
    history.replace(safePostLoginRedirect);
  }, [history, location.pathname, location.search, safePostLoginRedirect, shouldBypassAuthPages]);

  const contextValue = useMemo(
      () => ({
        toForgetPassword: `${url}/${forgotPassword}`,
        toActivateAccount: `${url}/${activateAccount}`,
      }),
      [url]
  );

  return (
      <AuthNavigationContext.Provider value={contextValue}>
        <Suspense fallback={null}>
          <Switch>
            <Route exact path={`${path}`}>
              <DefaultHeaderEmpty/>
              <Login/>
            </Route>
            <Route path={`${path}/${createAccount}`}>
              <DefaultHeaderEmpty/>
              <CreateAccountManagement/>
            </Route>
            <Route path={`${path}/${successfulAccountCreation}`}>
              <DefaultHeaderEmpty/>
              <SuccessfulAccountCreationPage/>
            </Route>

            <Route
                path={`${path}/${login}`}
                render={() => {
                  if (!auth.isUserLoggedIn) {
                    return <Login/>;
                  }
                  return null;
                }}
            />
            <Route path={`${path}/${otp}`}>
              <DefaultHeaderEmpty/>
              <OTP/>
            </Route>
            <Route path={`${path}/${forgotUsername}`}>
              <DefaultHeaderEmpty/>
              <ForgotUsername/>
            </Route>
            <Route path={`${path}/${forgotPassword}`}>
              <DefaultHeaderEmpty/>
              <ForgotPassword/>
            </Route>
            <Route path={`${path}/${resetPassword}`}>
              <DefaultHeaderEmpty/>
              <ResetPassword/>
            </Route>
            <Route path={`${path}/${setPassword}`}>
              <DefaultHeaderEmpty/>
              <SetPassword/>
            </Route>
            <Route path={`${path}/${activateAccount}`}>
              <DefaultHeaderEmpty/>
              <ActivateAccount/>
            </Route>
            <Route path={`${path}/${activated}`}>
              <DefaultHeaderEmpty/>
              <Activated/>
            </Route>
            <Route path={`${path}/${updatePassword.route}`}
                   render={
                      // WOGAA | CPEPOKPNEH-2464: The value for isUserPasswordExpired is set in OneTimePasswordForm.tsx page, after the user submitted the OTP.
                     // The value is always false if the user enter the Update Password page without going through the OTP page
                     () => isUserPasswordExpired
                         ? <> <DefaultHeaderEmpty/><UpdatePassword/></>
                          : <NavigateOnce to={`${path}/${login}`}/>
                   }
            />
            <Route path={`${path}/${unlockAccount}`}>
              <DefaultHeaderEmpty/>
              <UnlockAccount/>
            </Route>
            <Route path="*" exact>
              <NavigateOnce to={`/${errorRoute}`}/>
            </Route>
          </Switch>
        </Suspense>
      </AuthNavigationContext.Provider>
  );
};

export default AuthRoute;
